<script setup>
import { computed } from 'vue';
import XlButton from '../xl/XlButton.vue';
import XlDropdown from '../xl/XlDropdown.vue';
import XlTag from '../xl/XlTag.vue';

const props = defineProps({
  // Owners choose whose book they are looking at. A broker has no choice to
  // make — the server already confines them to their own — so they are told
  // the size of theirs instead.
  canChoose: { type: Boolean, default: false },
  brokers: { type: Array, default: () => [] },
  modelValue: { type: [String, Number], default: '' },
  bookSize: { type: Number, default: 0 },
});

const emit = defineEmits(['update:modelValue']);

/**
 * `name` is the contract, but the brokers API returns `first_name`/`last_name`
 * and callers have handed that straight in — which rendered every option blank,
 * silently, for as long as nobody looked at the dropdown. An option with no
 * label is never the right answer, so fall back rather than trust the shape.
 */
const labelFor = (broker) => broker.name
  || [broker.first_name, broker.last_name].filter(Boolean).join(' ')
  || broker.email
  || 'Unnamed broker';

const options = computed(() => [
  { value: '', label: 'All brokers' },
  { value: 'none', label: 'No broker yet' },
  ...props.brokers.map((broker) => ({ value: String(broker.id), label: labelFor(broker) })),
]);

// Filtering a handful of names is friction; filtering an agency's worth is the
// point. XlDropdown's own prop comment draws the line in the same place.
const searchable = computed(() => options.value.length > 8);
</script>

<template>
  <div class="broker-scope">
    <template v-if="props.canChoose">
      <XlDropdown
        aria-label="Whose book to show"
        data-testid="broker-scope"
        :options="options"
        :searchable="searchable"
        :model-value="String(props.modelValue ?? '')"
        @update:model-value="(value) => emit('update:modelValue', value)"
      />
      <XlButton
        v-if="String(props.modelValue ?? '') !== ''"
        variant="ghost"
        size="sm"
        data-testid="broker-scope-clear"
        @click="emit('update:modelValue', '')"
      >Clear</XlButton>
    </template>

    <XlTag v-else tone="brand" data-testid="broker-book">
      Your book<template v-if="props.bookSize !== null"> · {{ props.bookSize }} {{ props.bookSize === 1 ? 'policyholder' : 'policyholders' }}</template>
    </XlTag>
  </div>
</template>

<style scoped>
.broker-scope {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
</style>
